import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  downloadOrderReportExport,
  fetchOrderReport,
  type OrderReport,
  type OrderReportCategory,
} from "@/lib/adminOrdersReportApi";
import { fetchAdminCustomers } from "@/lib/adminCustomersApi";
import type { AdminClientSummary } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

const CATEGORY_TABS: { id: OrderReportCategory; label: string }[] = [
  { id: "TODOS", label: "Todos" },
  { id: "ENTREGUES", label: "Entregues" },
  { id: "CANCELADOS", label: "Cancelados" },
  { id: "ALTERADOS", label: "Alterados" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function customerLabel(c: AdminClientSummary): string {
  return c.tradeName || c.companyName || c.email;
}

function fmtQty(value: number): string {
  if (!value) return "—";
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function fmtMoney(value: number): string {
  return (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

function situationLabel(line: OrderReport["lines"][number]): string {
  const tags: string[] = [];
  if (line.delivered) tags.push("Entregue");
  if (line.cancelled) tags.push("Cancelado");
  if (line.altered) tags.push("Alterado");
  return tags.length > 0 ? tags.join(" + ") : line.status;
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminOrdersReport() {
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.PEDIDOS.EXPORTAR");
  const [from, setFrom] = useState(firstDayOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [customerId, setCustomerId] = useState<string>("");
  const [category, setCategory] = useState<OrderReportCategory>("TODOS");
  const [customers, setCustomers] = useState<AdminClientSummary[]>([]);
  const [report, setReport] = useState<OrderReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "csv" | null>(null);

  useEffect(() => {
    fetchAdminCustomers()
      .then((list) => setCustomers(list))
      .catch(() => {});
  }, []);

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => customerLabel(a).localeCompare(customerLabel(b))),
    [customers],
  );

  const handleGenerate = useCallback(async () => {
    if (!from || !to) {
      toast.error("Informe o período");
      return;
    }
    setLoading(true);
    try {
      const result = await fetchOrderReport({ from, to, customerId: customerId || null, category });
      setReport(result);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao gerar relatório"));
    } finally {
      setLoading(false);
    }
  }, [from, to, customerId, category]);

  const handleExport = useCallback(
    async (format: "pdf" | "csv") => {
      if (!from || !to) {
        toast.error("Informe o período");
        return;
      }
      setExporting(format);
      try {
        const { filename, blob } = await downloadOrderReportExport(
          { from, to, customerId: customerId || null, category },
          format,
        );
        downloadBlob(filename, blob);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Erro ao exportar"));
      } finally {
        setExporting(null);
      }
    },
    [from, to, customerId, category],
  );

  const totals = report?.totals;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-5 w-5" /> Relatório de Pedidos
            </h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Pedidos entregues, cancelados e alterados — por dia, período e cliente.
            </p>
          </div>
          {report && canExport && (
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("pdf")} disabled={exporting !== null}>
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("csv")} disabled={exporting !== null}>
                {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                CSV
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr_auto] items-end">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">De</Label>
            <DatePicker value={from} onChange={setFrom} />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Até</Label>
            <DatePicker value={to} onChange={setTo} />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Cliente</Label>
            <Select value={customerId || "__all__"} onValueChange={(v) => setCustomerId(v === "__all__" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os clientes</SelectItem>
                {sortedCustomers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{customerLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => void handleGenerate()} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
            Gerar
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t pt-3">
          <div className="inline-flex items-center gap-1 rounded-lg border bg-secondary/30 p-1">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setCategory(tab.id)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  category === tab.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {report && totals && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <StatCard label="Pedidos" value={String(report.orderCount)} />
          <StatCard label="Itens" value={String(totals.lineCount)} />
          <StatCard label="Valor total" value={fmtMoney(totals.totalValue)} />
        </div>
      )}

      {report && report.lines.length === 0 && (
        <div className="rounded-xl border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Nenhum pedido encontrado nesse filtro.
        </div>
      )}

      {report && report.lines.length > 0 && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Data</th>
                  <th className="px-4 py-2 text-left">Pedido</th>
                  <th className="px-4 py-2 text-left">Cliente</th>
                  <th className="px-4 py-2 text-left">Produto</th>
                  <th className="px-4 py-2 text-center">Unidade</th>
                  <th className="px-4 py-2 text-right">Quantidade</th>
                  <th className="px-4 py-2 text-right">Valor</th>
                  <th className="px-4 py-2 text-left">Situação</th>
                </tr>
              </thead>
              <tbody>
                {report.lines.map((l, idx) => (
                  <tr key={`${l.orderId}-${l.productName}-${idx}`} className="border-t">
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDate(l.date)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{l.orderNumber ? `#${l.orderNumber}` : "—"}</td>
                    <td className="px-4 py-2">{l.clientName ?? "—"}</td>
                    <td className="px-4 py-2">{l.productName}</td>
                    <td className="px-4 py-2 text-center text-muted-foreground">{l.unit}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtQty(l.quantity)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(l.value)}</td>
                    <td className="px-4 py-2 text-xs">{situationLabel(l)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!report && !loading && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 py-16 text-center text-sm text-muted-foreground">
          <Download className="h-6 w-6" />
          Informe o período e clique em <strong>Gerar</strong> para ver o relatório.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
